import { useState, useEffect, useCallback } from 'react';
import {
  DatabaseSchema,
  WeightEntry,
  MealEntry,
  ExerciseEntry,
  SupplementEntry,
  BodyMeasurementEntry,
  PhotoEntry,
  GoalProgress,
  UserPreferences,
  WorkoutTemplate,
  BloodPressureEntry,
  LabResultEntry,
  WalkEntry,
  HealthRiskSnapshot,
  FoodLogEntry,
} from '../types';
import { loadDatabase, saveDatabase, isProduction } from '../utils/fileStorage';
import { getSessionToken } from '../utils/auth';

export const useOptimizedData = () => {
  const [database, setDatabase] = useState<DatabaseSchema | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshDatabase = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await loadDatabase();
      setDatabase(data);
    } catch (err) {
      console.error('Error loading database:', err);
      setError('Failed to load data');
      
      // If we're in production and get an auth error, clear tokens
      if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
        console.warn('Clearing authentication tokens due to database load failure');
        localStorage.removeItem('stride_auth_token');
        localStorage.removeItem('stride_auth_timestamp');
        localStorage.removeItem('stride_authenticated');
        // Force page reload to show login screen
        window.location.reload();
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshDatabase();
  }, [refreshDatabase]);

  const updateDatabase = useCallback(async (updater: (db: DatabaseSchema) => DatabaseSchema) => {
    if (!database) return;

    try {
      const updatedDb = updater(database);
      await saveDatabase(updatedDb);
      setDatabase(updatedDb);
    } catch (err) {
      console.error('Error updating database:', err);
      setError('Failed to save data');
    }
  }, [database]);

  const addWeight = useCallback(async (weight: WeightEntry) => {
    await updateDatabase(db => ({
      ...db,
      weights: [...db.weights.filter(w => w.id !== weight.id), weight],
      goals: db.goals ? { ...db.goals, currentWeight: weight.weight } : db.goals
    }));
  }, [updateDatabase]);

  const addMeal = useCallback(async (meal: MealEntry) => {
    await updateDatabase(db => ({
      ...db,
      meals: [...db.meals.filter(m => m.id !== meal.id), meal]
    }));
  }, [updateDatabase]);

  const addExercise = useCallback(async (exercise: ExerciseEntry) => {
    await updateDatabase(db => ({
      ...db,
      exercises: [...db.exercises.filter(e => e.id !== exercise.id), exercise]
    }));
  }, [updateDatabase]);

  const addSupplement = useCallback(async (supplement: SupplementEntry) => {
    await updateDatabase(db => ({
      ...db,
      supplements: [...db.supplements.filter(s => s.id !== supplement.id), supplement]
    }));
  }, [updateDatabase]);

  const addBodyMeasurement = useCallback(async (measurement: BodyMeasurementEntry) => {
    await updateDatabase(db => ({
      ...db,
      bodyMeasurements: [...db.bodyMeasurements.filter(m => m.id !== measurement.id), measurement]
    }));
  }, [updateDatabase]);

  const addPhoto = useCallback(async (photo: PhotoEntry) => {
    await updateDatabase(db => ({
      ...db,
      photos: [...db.photos.filter(p => p.id !== photo.id), photo]
    }));
  }, [updateDatabase]);

  const updateGoals = useCallback(async (goals: GoalProgress) => {
    if (isProduction()) {
      // Use individual API endpoint with sync logic
      try {
        const sessionToken = getSessionToken();
        const response = await fetch('/api/goals', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Session-Token': sessionToken || '',
          },
          body: JSON.stringify(goals),
        });

        if (!response.ok) {
          throw new Error(`Failed to update goals: ${response.statusText}`);
        }

        // Refresh database to get synced data
        await refreshDatabase();
      } catch (error) {
        console.error('Error updating goals:', error);
        throw error;
      }
    } else {
      // Development mode - use bulk update
      await updateDatabase(db => ({
        ...db,
        goals
      }));
    }
  }, [updateDatabase, refreshDatabase]);

  const updatePreferences = useCallback(async (preferences: UserPreferences) => {
    if (isProduction()) {
      // Use individual API endpoint with sync logic
      try {
        const sessionToken = getSessionToken();
        const response = await fetch('/api/preferences', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Session-Token': sessionToken || '',
          },
          body: JSON.stringify(preferences),
        });

        if (!response.ok) {
          throw new Error(`Failed to update preferences: ${response.statusText}`);
        }

        // Refresh database to get synced data
        await refreshDatabase();
      } catch (error) {
        console.error('Error updating preferences:', error);
        throw error;
      }
    } else {
      // Development mode - use bulk update
      await updateDatabase(db => ({
        ...db,
        preferences
      }));
    }
  }, [updateDatabase, refreshDatabase]);

  // Template management
  const updateWorkoutTemplates = useCallback(async (templates: WorkoutTemplate[]) => {
    await updateDatabase(db => ({
      ...db,
      workoutTemplates: templates
    }));
  }, [updateDatabase]);

  // Delete functions
  const deleteWeight = useCallback(async (id: string) => {
    await updateDatabase(db => ({
      ...db,
      weights: db.weights.filter(w => w.id !== id)
    }));
  }, [updateDatabase]);

  const deleteMeal = useCallback(async (id: string) => {
    await updateDatabase(db => ({
      ...db,
      meals: db.meals.filter(m => m.id !== id)
    }));
  }, [updateDatabase]);

  const deleteExercise = useCallback(async (id: string) => {
    await updateDatabase(db => ({
      ...db,
      exercises: db.exercises.filter(e => e.id !== id)
    }));
  }, [updateDatabase]);

  const deleteSupplement = useCallback(async (id: string) => {
    await updateDatabase(db => ({
      ...db,
      supplements: db.supplements.filter(s => s.id !== id)
    }));
  }, [updateDatabase]);

  const deleteBodyMeasurement = useCallback(async (id: string) => {
    await updateDatabase(db => ({
      ...db,
      bodyMeasurements: db.bodyMeasurements.filter(m => m.id !== id)
    }));
  }, [updateDatabase]);

  const deletePhoto = useCallback(async (id: string) => {
    await updateDatabase(db => ({
      ...db,
      photos: db.photos.filter(p => p.id !== id)
    }));
  }, [updateDatabase]);

  const addBloodPressure = useCallback(async (entry: BloodPressureEntry) => {
    await updateDatabase(db => ({
      ...db,
      bloodPressure: [...db.bloodPressure.filter(e => e.id !== entry.id), entry],
    }));
  }, [updateDatabase]);

  const deleteBloodPressure = useCallback(async (id: string) => {
    await updateDatabase(db => ({ ...db, bloodPressure: db.bloodPressure.filter(e => e.id !== id) }));
  }, [updateDatabase]);

  const addLabResult = useCallback(async (entry: LabResultEntry) => {
    await updateDatabase(db => ({
      ...db,
      labResults: [...db.labResults.filter(e => e.id !== entry.id), entry],
    }));
  }, [updateDatabase]);

  const deleteLabResult = useCallback(async (id: string) => {
    await updateDatabase(db => ({ ...db, labResults: db.labResults.filter(e => e.id !== id) }));
  }, [updateDatabase]);

  const addWalk = useCallback(async (entry: WalkEntry) => {
    await updateDatabase(db => ({
      ...db,
      walks: [...db.walks.filter(e => e.id !== entry.id), entry],
    }));
  }, [updateDatabase]);

  const deleteWalk = useCallback(async (id: string) => {
    await updateDatabase(db => ({ ...db, walks: db.walks.filter(e => e.id !== id) }));
  }, [updateDatabase]);

  const addHealthRiskSnapshot = useCallback(async (entry: HealthRiskSnapshot) => {
    await updateDatabase(db => ({
      ...db,
      healthRiskSnapshots: [...db.healthRiskSnapshots.filter(e => e.id !== entry.id), entry],
    }));
  }, [updateDatabase]);

  const deleteHealthRiskSnapshot = useCallback(async (id: string) => {
    await updateDatabase(db => ({ ...db, healthRiskSnapshots: db.healthRiskSnapshots.filter(e => e.id !== id) }));
  }, [updateDatabase]);

  const addFoodLog = useCallback(async (entry: FoodLogEntry) => {
    await updateDatabase(db => ({
      ...db,
      foodLog: [...db.foodLog.filter(e => e.id !== entry.id), entry],
    }));
  }, [updateDatabase]);

  const deleteFoodLog = useCallback(async (id: string) => {
    await updateDatabase(db => ({ ...db, foodLog: db.foodLog.filter(e => e.id !== id) }));
  }, [updateDatabase]);

  // Cleanup function to remove duplicate weight entries, keeping the most recent one for each date
  const cleanupDuplicateWeights = useCallback(async () => {
    await updateDatabase(db => {
      const weightsByDate = new Map<string, WeightEntry>();
      
      // Keep only the most recent entry for each date (highest timestamp in ID)
      db.weights.forEach(weight => {
        const existing = weightsByDate.get(weight.date);
        if (!existing || weight.id > existing.id) {
          weightsByDate.set(weight.date, weight);
        }
      });
      
      return {
        ...db,
        weights: Array.from(weightsByDate.values()).sort((a, b) => 
          new Date(b.date).getTime() - new Date(a.date).getTime()
        )
      };
    });
  }, [updateDatabase]);

  return {
    database,
    loading,
    error,
    refreshDatabase,
    addWeight,
    addMeal,
    addExercise,
    addSupplement,
    addBodyMeasurement,
    addPhoto,
    updateGoals,
    updatePreferences,
    updateWorkoutTemplates,
    deleteWeight,
    deleteMeal,
    deleteExercise,
    deleteSupplement,
    deleteBodyMeasurement,
    deletePhoto,
    cleanupDuplicateWeights,
    addBloodPressure,
    deleteBloodPressure,
    addLabResult,
    deleteLabResult,
    addWalk,
    deleteWalk,
    addHealthRiskSnapshot,
    deleteHealthRiskSnapshot,
    addFoodLog,
    deleteFoodLog,
  };
};